import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { components } from '@/types/api'
import { getDefaultCurrency } from '@/utils/currencyDefaults'
import {
  initAuthServiceWorker,
  updateSessionInServiceWorker,
  clearSessionInServiceWorker,
  addServiceWorkerMessageHandler,
} from '@/utils/authServiceWorker'

// Currency can be either a string (legacy) or object (new format)
interface CurrencyObject {
  code: string
  symbol: string
  decimal_places: number
}

// Import the proper types
import type { OrganizationDetailed, DeliveryZone } from '@/types/index'

// Define the user type based on what the auth API actually returns (UserWithDetailedOrganization)
interface User {
  id: string
  username: string
  full_name: string
  role: 'admin' | 'sales' | 'deliverer' | 'accountant'
  preferred_lang?: string
  lang?: string
  organization_lang?: string
  organization: OrganizationDetailed
  org_branch_id?: string // Optional: user's assigned branch
}

interface TokenInfo {
  token: string
  expiresAt: number // Unix timestamp
}

// ============================================
// STORAGE KEYS
// ============================================
// SECURITY: Access token is MEMORY-ONLY (never in localStorage)
// SECURITY: Refresh token is HTTP-ONLY COOKIE ONLY (never in localStorage)
// Only user data and token expiry are stored for session recovery
const USER_STORAGE_KEY = 'hudstore_user_data'
const TOKEN_EXPIRY_STORAGE_KEY = 'hudstore_token_expiry' // Only stores expiry, not token
const ORGANIZATION_STORAGE_KEY = 'hudstore_organization_data'
const ORGANIZATION_ZONES_KEY = 'hudstore_organization_zones'
const SESSION_ID_KEY = 'hudstore_session_id' // For rotation detection (not the token itself)

// Legacy keys for migration (will be cleaned up)
const LEGACY_TOKEN_STORAGE_KEY = 'hudstore_access_token'
const LEGACY_TOKEN_INFO_STORAGE_KEY = 'hudstore_token_info'
const LEGACY_REFRESH_TOKEN_KEY = 'hudstore_refresh_token'
const LEGACY_LAST_REFRESH_TOKEN_KEY = 'hudstore_last_refresh_token'

// ============================================
// CONFIGURATION CONSTANTS
// ============================================
const REFRESH_CHECK_INTERVAL_MS = 60 * 1000 // 60 seconds (balanced for efficiency)
const REFRESH_WINDOW_MS = 5 * 60 * 1000 // 5 minutes before expiry
const ACTIVITY_REFRESH_THRESHOLD_MS = 10 * 60 * 1000 // Refresh if active and <10 min left
const MAX_BACKOFF_DELAY_MS = 5 * 60 * 1000 // Max 5 minutes between retry attempts
const INITIAL_BACKOFF_DELAY_MS = 1000 // Start with 1 second
const ACTIVITY_DEBOUNCE_MS = 30 * 1000 // Only check activity every 30 seconds

interface OrganizationZone {
  label: string
  value: string
  id: string
  zone_bucket: string
  delivery_cost: string
  description: string
}

export const useAuthStore = defineStore('auth', () => {
  // ============================================
  // STATE
  // ============================================
  const user = ref<User | null>(null)
  const organization = ref<OrganizationDetailed | null>(null)
  const organizationZones = ref<OrganizationZone[]>([])
  const currentBranch = ref<any | null>(null) // OrgBranch type

  // Access token is MEMORY-ONLY for security (XSS protection)
  const token = ref<string | null>(null)
  const tokenExpiresAt = ref<number | null>(null)

  // Refresh token is HTTP-ONLY COOKIE ONLY (never exposed to JavaScript)
  // We only track a session ID for rotation detection
  const hasActiveSession = ref(false) // Tracks if we believe we have a valid session
  const sessionId = ref<string | null>(null) // Unique ID per session for rotation detection

  // Memory-only mode: when true, no auth state is persisted to localStorage and
  // initAuth performs no restore. Used by the embedded landing demo so its
  // (same-origin) iframe never reads or clobbers the parent tab's real session.
  const memoryOnly = ref(false)
  const setMemoryOnly = (value: boolean = true) => {
    memoryOnly.value = value
  }

  // Refresh state management
  const isRefreshing = ref(false)
  const refreshFailureCount = ref(0) // For exponential backoff
  const lastRefreshAttempt = ref<number>(0)
  const lastActivityTime = ref<number>(Date.now()) // For activity-based refresh

  // ============================================
  // COMPUTED PROPERTIES
  // ============================================
  const isAuthenticated = computed(() => !!token.value && !!user.value)

  const isTokenExpired = computed(() => {
    if (!tokenExpiresAt.value) return false
    return Date.now() >= tokenExpiresAt.value
  })

  const isTokenExpiringSoon = computed(() => {
    if (!tokenExpiresAt.value) return false
    return Date.now() >= tokenExpiresAt.value - REFRESH_WINDOW_MS
  })

  const timeUntilExpiry = computed(() => {
    if (!tokenExpiresAt.value) return 0
    return Math.max(0, tokenExpiresAt.value - Date.now())
  })

  // Exponential backoff delay calculation
  const currentBackoffDelay = computed(() => {
    if (refreshFailureCount.value === 0) return 0
    const delay = INITIAL_BACKOFF_DELAY_MS * Math.pow(2, refreshFailureCount.value - 1)
    return Math.min(delay, MAX_BACKOFF_DELAY_MS)
  })

  const canAttemptRefresh = computed(() => {
    if (refreshFailureCount.value === 0) return true
    const timeSinceLastAttempt = Date.now() - lastRefreshAttempt.value
    return timeSinceLastAttempt >= currentBackoffDelay.value
  })

  const userRole = computed(() => user.value?.role)
  const userOrganization = computed(() => user.value?.organization)
  const userDisplayName = computed(() => {
    if (!user.value) return ''
    return user.value.full_name || user.value.username
  })

  // Role-based permissions
  const hasRole = (role: string | string[]) => {
    if (!user.value) return false
    const userRole = user.value.role
    return Array.isArray(role) ? role.includes(userRole) : userRole === role
  }

  const isAdmin = computed(() => hasRole('admin'))
  const canManageUsers = computed(() => hasRole(['admin', 'sales']))
  const canViewDeliveries = computed(() => hasRole(['admin', 'sales', 'deliverer']))
  const canViewFinances = computed(() => hasRole(['admin', 'accountant']))

  // ============================================
  // TOKEN MANAGEMENT (Memory-only for security)
  // ============================================
  const setToken = (authToken: string, expiresIn?: number) => {
    // Store token in MEMORY ONLY (not localStorage) for XSS protection
    token.value = authToken

    if (expiresIn) {
      // Convert seconds to milliseconds and add current time
      const expirationTime = Date.now() + expiresIn * 1000
      tokenExpiresAt.value = expirationTime

      // Only store expiry time (not the token itself) for session recovery
      if (!memoryOnly.value)
        localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expirationTime.toString())
    }

    // Reset backoff on successful token set
    refreshFailureCount.value = 0
  }

  const setUser = async (userData: User) => {
    user.value = userData
    if (!memoryOnly.value) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData))

    // Fetch user's branch if they have one assigned
    if (userData.org_branch_id) {
      try {
        const { apiClient } = await import('@/services/api')
        const response = await apiClient.client
          .get(apiClient.url(`org_branches/${userData.org_branch_id}`))
          .json<{ data: Record<string, unknown> }>()
        currentBranch.value = response.data
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Failed to fetch user branch:', error)
        }
        currentBranch.value = null
      }
    } else {
      currentBranch.value = null
    }

    // Also store organization data separately for easy access
    if (userData.organization) {
      const orgData = { ...userData.organization }

      // Currency should now always be provided by the API
      // If not provided, fall back to browser detection as backup
      if (!orgData.currency) {
        const detectedCurrency = getDefaultCurrency()
        orgData.currency = detectedCurrency
      }

      organization.value = orgData
      if (!memoryOnly.value) localStorage.setItem(ORGANIZATION_STORAGE_KEY, JSON.stringify(orgData))

      // Load delivery zones from organization data if available
      if (orgData.delivery_zones && orgData.delivery_zones.length > 0) {
        const zones = orgData.delivery_zones.map((zone: DeliveryZone) => ({
          label: zone.name,
          value: zone.name,
          id: zone.id,
          zone_bucket: zone.zone_bucket,
          delivery_cost: zone.delivery_cost,
          description: zone.description,
        }))
        setOrganizationZones(zones)
      }
    }
  }

  const updateUser = (updates: Partial<User>) => {
    if (user.value) {
      user.value = { ...user.value, ...updates }
      if (!memoryOnly.value) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user.value))

      // Update organization data if it was modified
      if (updates.organization) {
        organization.value = updates.organization
        if (!memoryOnly.value)
          localStorage.setItem(ORGANIZATION_STORAGE_KEY, JSON.stringify(updates.organization))

        // Update delivery zones from organization data if available
        if (updates.organization.delivery_zones && updates.organization.delivery_zones.length > 0) {
          const zones = updates.organization.delivery_zones.map((zone: DeliveryZone) => ({
            label: zone.name,
            value: zone.name,
            id: zone.id,
            zone_bucket: zone.zone_bucket,
            delivery_cost: zone.delivery_cost,
            description: zone.description,
          }))
          setOrganizationZones(zones)
        }
      }
    }
  }

  const clearAuth = () => {
    // Clear memory state
    user.value = null
    organization.value = null
    organizationZones.value = []
    currentBranch.value = null
    token.value = null
    tokenExpiresAt.value = null
    hasActiveSession.value = false
    sessionId.value = null
    refreshFailureCount.value = 0
    lastRefreshAttempt.value = 0

    // Clear service worker session
    clearSessionInServiceWorker()

    // Guarded for non-DOM contexts (SSR, web workers, vitest teardown
    // where the isTokenExpired watcher can resolve after happy-dom is gone).
    if (typeof localStorage === 'undefined') return

    // Clear current storage keys
    localStorage.removeItem(USER_STORAGE_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY)
    localStorage.removeItem(SESSION_ID_KEY)
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY)
    localStorage.removeItem(ORGANIZATION_ZONES_KEY)

    // Clear legacy storage keys for backward compatibility
    localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY)
    localStorage.removeItem(LEGACY_TOKEN_INFO_STORAGE_KEY)
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY)
    localStorage.removeItem(LEGACY_LAST_REFRESH_TOKEN_KEY)
    localStorage.removeItem('access_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('token_info')
  }

  // BroadcastChannel for cross-tab logout sync. CustomEvent only fires in
  // the same window, so a logout in tab A wouldn't kick out tab B until
  // the next session-id poll (~60s). BroadcastChannel makes this near-
  // instant. Lazily initialised the first time we need it; guarded for
  // environments without the API (jsdom <22, SSR, web workers).
  let authChannel: BroadcastChannel | null = null
  const getAuthChannel = (): BroadcastChannel | null => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return null
    if (!authChannel) {
      authChannel = new BroadcastChannel('hudstore-auth')
      authChannel.addEventListener('message', (event) => {
        if (event.data?.type === 'logout') {
          // Another tab logged out — clear local auth state without re-
          // broadcasting (would loop), then force this tab off any
          // protected route. Relying on the 60s session poll left the
          // other tab sitting on authenticated content until the next
          // navigation; redirect immediately so logout is near-instant
          // across tabs.
          clearAuth()
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:logout'))
            const path = window.location.pathname
            if (path !== '/login' && path !== '/') {
              window.location.href = '/login'
            }
          }
        }
      })
    }
    return authChannel
  }

  const logout = async () => {
    clearAuth()

    // Clear pending magic links on logout
    try {
      const { clearAllMagicLinks } = await import('@/utils/magicLinkStorage')
      clearAllMagicLinks()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to clear magic links on logout:', error)
      }
    }

    // Notify other tabs via BroadcastChannel (cross-tab) and same-tab
    // listeners via CustomEvent (in-window). Guarded for non-DOM contexts.
    const channel = getAuthChannel()
    if (channel) {
      try {
        channel.postMessage({ type: 'logout', at: Date.now() })
      } catch {
        // Channel closed or browser refused — fall through to CustomEvent.
      }
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:logout'))
    }
  }

  const setAuth = (userData: User, authToken: string, expiresIn?: number) => {
    // Set the token before setUser: setUser fires an authenticated
    // GET org_branches/:id, so the token must already be in the store to avoid a
    // spurious 401 + refresh round-trip on that bootstrap request.
    // (Note: deliverers are intentionally signed out of the web app in
    // useLogin#onSuccess — they are mobile-only — so a deliverer staying on
    // /login is by design, not a bug.)
    setToken(authToken, expiresIn)
    setUser(userData)
    // No need to load organization zones separately since they're now part of login response
    // loadOrganizationZones()
    // Dispatch custom event
    window.dispatchEvent(
      new CustomEvent('auth:login', {
        detail: { user: userData, token: authToken },
      }),
    )
  }

  // Generate a unique session ID (not the token, just an identifier)
  const generateSessionId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
  }

  const setActiveSession = (isActive: boolean = true) => {
    hasActiveSession.value = isActive

    if (isActive) {
      // Generate new session ID if we don't have one
      if (!sessionId.value) {
        sessionId.value = generateSessionId()
        if (!memoryOnly.value) localStorage.setItem(SESSION_ID_KEY, sessionId.value)
      }
    } else {
      sessionId.value = null
      localStorage.removeItem(SESSION_ID_KEY)
    }
  }

  // Detect if session was invalidated (e.g., by another tab logging out)
  const detectSessionInvalidation = (): boolean => {
    // Memory-only sessions (embedded landing demo) are intentionally never
    // mirrored to localStorage, so a stored-id comparison always mismatches and
    // would spuriously log the demo out on the first token refresh. Skip it.
    if (memoryOnly.value) return false

    const storedSessionId = localStorage.getItem(SESSION_ID_KEY)

    // If we have a session but localStorage doesn't match, session was invalidated elsewhere
    if (hasActiveSession.value && storedSessionId !== sessionId.value) {
      if (import.meta.env.DEV) {
        console.warn('Session invalidation detected. Another tab may have logged out.')
      }
      return true
    }
    return false
  }

  // Legacy compatibility: setRefreshToken now just marks session as active
  // The actual refresh token is handled via HTTP-only cookies by the backend
  const setRefreshToken = (_tokenValue: string) => {
    // We no longer store the refresh token in JavaScript
    // Just mark that we have an active session
    setActiveSession(true)
  }

  const setOrganizationZones = (zones: OrganizationZone[]) => {
    organizationZones.value = zones
    if (!memoryOnly.value) localStorage.setItem(ORGANIZATION_ZONES_KEY, JSON.stringify(zones))
  }

  const loadOrganizationZones = async () => {
    if (!token.value) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'}/delivery_zones`,
        {
          headers: {
            Authorization: `Bearer ${token.value}`,
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.ok) {
        const data = await response.json()
        const zones =
          data.data?.map((zone: DeliveryZone) => ({
            label: zone.name,
            value: zone.name,
            id: zone.id,
            zone_bucket: zone.zone_bucket,
            delivery_cost: zone.delivery_cost,
            description: zone.description,
          })) || []

        setOrganizationZones(zones)
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to load organization zones:', error)
      }
      // Fallback to empty array on error
      setOrganizationZones([])
    }
  }

  const initAuth = () => {
    // Embedded demo: never hydrate from shared storage. The iframe must not
    // adopt the parent tab's real session.
    if (memoryOnly.value) return

    try {
      // ============================================
      // STEP 0: Register cross-tab logout listener
      // ============================================
      // Instantiate the BroadcastChannel eagerly so EVERY tab listens for
      // 'logout' messages from the moment it boots. Previously the channel
      // was created lazily inside logout(), so a tab that never logged out
      // itself never registered the listener — cross-tab logout sync silently
      // did nothing in the receiving tab.
      getAuthChannel()

      // ============================================
      // STEP 1: Clean up legacy storage (migration)
      // ============================================
      // Remove any tokens stored in localStorage (security upgrade)
      localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY)
      localStorage.removeItem(LEGACY_TOKEN_INFO_STORAGE_KEY)
      localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY)
      localStorage.removeItem(LEGACY_LAST_REFRESH_TOKEN_KEY)
      localStorage.removeItem('access_token')
      localStorage.removeItem('token_info')

      // ============================================
      // STEP 2: Load user data
      // ============================================
      let savedUser = localStorage.getItem(USER_STORAGE_KEY)
      if (!savedUser) {
        savedUser = localStorage.getItem('user_data') // Legacy fallback
        if (savedUser) {
          // Migrate to new key
          localStorage.setItem(USER_STORAGE_KEY, savedUser)
          localStorage.removeItem('user_data')
        }
      }

      if (savedUser) {
        const userData = JSON.parse(savedUser)
        user.value = userData

        if (userData.organization) {
          organization.value = userData.organization
        }
      }

      // ============================================
      // STEP 2b: Enforce deliverer web-block on recovery
      // ============================================
      // Deliverers are intentionally barred from the web app (mobile-only).
      // useLogin().onSuccess enforces this at LOGIN, but session recovery from
      // localStorage bypasses that path — without this gate a deliverer whose
      // session was persisted (e.g. role flipped, or pre-block build) would be
      // silently re-hydrated on reload and see the web UI until expiry.
      // clearAuth() purges the restored session + storage + SW state locally,
      // but deliberately does NOT revoke the server-side refresh cookie, so the
      // user's legitimate MOBILE session stays intact.
      if (user.value?.role === 'deliverer') {
        clearAuth()
        return
      }

      // Load organization data (if stored separately)
      const savedOrganization = localStorage.getItem(ORGANIZATION_STORAGE_KEY)
      if (savedOrganization) {
        organization.value = JSON.parse(savedOrganization)
      }

      // Load organization zones
      const savedZones = localStorage.getItem(ORGANIZATION_ZONES_KEY)
      if (savedZones) {
        try {
          organizationZones.value = JSON.parse(savedZones)
        } catch {
          organizationZones.value = []
        }
      }

      // Self-heal: zones ship inside the login payload (organization.delivery_zones)
      // but were historically only persisted under their own key. If that key is
      // empty/stale on reload, derive them from the restored organization so the
      // delivery-zone dropdown isn't blank for already-logged-in sessions.
      if (organizationZones.value.length === 0) {
        const restoredZones = (organization.value as { delivery_zones?: DeliveryZone[] } | null)
          ?.delivery_zones
        if (restoredZones?.length) {
          setOrganizationZones(
            restoredZones.map((zone) => ({
              label: zone.name,
              value: zone.name,
              id: zone.id,
              zone_bucket: zone.zone_bucket,
              delivery_cost: zone.delivery_cost,
              description: zone.description,
            })),
          )
        }
      }

      // ============================================
      // STEP 3: Load session ID for rotation detection
      // ============================================
      const savedSessionId = localStorage.getItem(SESSION_ID_KEY)
      if (savedSessionId) {
        sessionId.value = savedSessionId
        hasActiveSession.value = true
      }

      // ============================================
      // STEP 4: Handle session recovery
      // ============================================
      // With memory-only tokens and HTTP-only cookies:
      // - Access token is lost on page reload (by design)
      // - Refresh token is in HTTP-only cookie (handled by browser)
      // - We attempt silent refresh via iframe on init if we had a session

      const savedExpiry = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY)
      if (savedExpiry && savedSessionId && user.value) {
        const expiryTime = parseInt(savedExpiry, 10)

        // We had an active session, attempt to recover it
        hasActiveSession.value = true
        tokenExpiresAt.value = expiryTime

        // Token will be refreshed via iframe when auto-refresh starts
        // or on first API call if the cookie is still valid
      } else if (user.value && savedSessionId) {
        // Had a session but no expiry info - try to refresh
        hasActiveSession.value = true
        tokenExpiresAt.value = 0 // Mark as expired to trigger immediate refresh
      } else {
        // No valid session found
        clearAuth()
      }

      // ============================================
      // STEP 5: Initialize Service Worker (A+ Feature)
      // ============================================
      initAuthServiceWorker().then((success) => {
        if (success && hasActiveSession.value) {
          // Update service worker with current session state
          updateSessionInServiceWorker(tokenExpiresAt.value, true)

          // Listen for background refresh messages
          addServiceWorkerMessageHandler((message) => {
            if (message.type === 'TOKEN_REFRESHED' && message.payload.access_token) {
              // Update token from background refresh
              setAuth(
                message.payload.user,
                message.payload.access_token,
                message.payload.expires_in,
              )
            } else if (message.type === 'SESSION_EXPIRED') {
              // Session expired in background, logout
              logout()
            }
          })
        }
      })
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to initialize auth:', error)
      }
      clearAuth()
    }
  }

  const setRefreshing = (refreshing: boolean) => {
    isRefreshing.value = refreshing
  }

  const updateUserLanguage = async (language: string): Promise<boolean> => {
    if (!user.value || !token.value) return false

    try {
      // Import dynamically to avoid circular dependency
      const { apiClient } = await import('@/services/api')
      const response = await apiClient.updateUserSettings({ lang: language })

      if (response.success) {
        // Update the user's language preference locally
        if (user.value) {
          user.value.preferred_lang = language
        }
        return true
      }
      return false
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to update user language preference:', error)
      }
      return false
    }
  }

  // ============================================
  // TOKEN REFRESH WITH EXPONENTIAL BACKOFF
  // ============================================
  const refreshTokenIfNeeded = async (): Promise<boolean> => {
    // If no active session, we can't refresh
    if (!hasActiveSession.value && !token.value) {
      return false
    }

    // If token exists and not expiring soon, we're good
    if (token.value && !isTokenExpiringSoon.value && !isTokenExpired.value) {
      return true
    }

    // Check for session invalidation (another tab logged out)
    if (detectSessionInvalidation()) {
      logout()
      return false
    }

    // Check exponential backoff
    if (!canAttemptRefresh.value) {
      const waitTime = Math.ceil(
        (currentBackoffDelay.value - (Date.now() - lastRefreshAttempt.value)) / 1000,
      )
      return !!token.value && !isTokenExpired.value
    }

    // Wait for existing refresh to complete
    if (isRefreshing.value) {
      let attempts = 0
      while (isRefreshing.value && attempts < 100) {
        // Max 10 seconds
        await new Promise((resolve) => setTimeout(resolve, 100))
        attempts++
      }
      return !!token.value && !isTokenExpired.value
    }

    try {
      isRefreshing.value = true
      lastRefreshAttempt.value = Date.now()

      // Import dynamically to avoid circular dependency
      const { apiClient } = await import('@/services/api')

      // Refresh token is in HTTP-only cookie, sent automatically by browser
      // We don't send it in the body anymore (security improvement)
      // API wraps payload in { data: ... } envelope via render_data helper.
      const envelope = await apiClient.client.post(apiClient.url('refresh')).json<{
        data: {
          user: User
          access_token: string
          expires_in: number
          refresh_token?: string
        }
      }>()
      const response = envelope.data

      // Mark session as active (refresh token was rotated by backend in cookie)
      setActiveSession(true)

      setAuth(response.user, response.access_token, response.expires_in)

      // Reset backoff on success
      refreshFailureCount.value = 0

      return true
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status

      // A 401 means there is no valid refresh session to recover — the normal
      // case when a stale session flag triggers a recovery refresh (e.g. landing
      // on /login after a hard refresh). Clear the stale session so we stop
      // retrying on every navigation, and don't surface it as an error.
      if (status === 401) {
        setActiveSession(false)
        return false
      }

      if (import.meta.env.DEV) {
        console.warn('Token refresh failed:', error)
      }

      // Increment failure count for exponential backoff
      refreshFailureCount.value = Math.min(refreshFailureCount.value + 1, 10) // Cap at 10

      // Don't logout here - let the caller decide
      return false
    } finally {
      isRefreshing.value = false
    }
  }

  // ============================================
  // AUTO REFRESH WITH ACTIVITY TRACKING
  // ============================================
  let refreshInterval: number | null = null
  let activityListenersSetup = false

  // Track user activity for activity-based session extension
  const updateActivityTime = () => {
    lastActivityTime.value = Date.now()
  }

  // Debounced activity handler
  let activityDebounceTimer: number | null = null
  const handleUserActivity = () => {
    if (activityDebounceTimer) return

    activityDebounceTimer = window.setTimeout(() => {
      updateActivityTime()

      // If user is active and token is expiring within activity threshold, refresh proactively
      if (
        isAuthenticated.value &&
        !isRefreshing.value &&
        timeUntilExpiry.value < ACTIVITY_REFRESH_THRESHOLD_MS
      ) {
        refreshTokenIfNeeded()
      }

      activityDebounceTimer = null
    }, ACTIVITY_DEBOUNCE_MS)
  }

  const setupActivityListeners = () => {
    if (activityListenersSetup || typeof window === 'undefined') return

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true })
    })

    activityListenersSetup = true
  }

  const removeActivityListeners = () => {
    if (!activityListenersSetup || typeof window === 'undefined') return

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach((event) => {
      window.removeEventListener(event, handleUserActivity)
    })

    if (activityDebounceTimer) {
      clearTimeout(activityDebounceTimer)
      activityDebounceTimer = null
    }

    activityListenersSetup = false
  }

  const startAutoRefresh = () => {
    if (refreshInterval) clearInterval(refreshInterval)

    // Setup activity tracking
    setupActivityListeners()

    // If we don't have an access token but have an active session, refresh immediately
    if (!token.value && hasActiveSession.value) {
      // Use iframe-based silent refresh for initial token acquisition
      performSilentRefresh()
    }

    // Check every 60 seconds if we need to refresh
    refreshInterval = window.setInterval(async () => {
      if (!isAuthenticated.value && !hasActiveSession.value) {
        stopAutoRefresh()
        return
      }

      // Check for session invalidation from another tab
      if (detectSessionInvalidation()) {
        stopAutoRefresh()
        logout()
        return
      }

      // Need refresh if token is expiring soon OR we don't have a token but have a session
      const needsRefresh = isTokenExpiringSoon.value || (!token.value && hasActiveSession.value)

      if (needsRefresh && !isRefreshing.value && canAttemptRefresh.value) {
        // Prefer iframe-based silent refresh
        const success = await performSilentRefresh()
        if (!success && isTokenExpired.value && !hasActiveSession.value) {
          // Only logout if refresh failed, token expired, and no active session
          stopAutoRefresh()
          logout()
        }
      }
    }, REFRESH_CHECK_INTERVAL_MS)
  }

  const stopAutoRefresh = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
    removeActivityListeners()
  }

  // ============================================
  // SILENT REFRESH VIA IFRAME (A+ Feature)
  // ============================================
  const getApiBaseUrl = () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1'
    // If relative URL, construct absolute URL from current origin
    if (baseUrl.startsWith('/')) {
      return `${window.location.origin}${baseUrl}`
    }
    return baseUrl
  }

  const SILENT_REFRESH_URL = `${getApiBaseUrl()}/silent_refresh`
  const SILENT_REFRESH_TIMEOUT_MS = 10000 // 10 seconds timeout

  let silentRefreshIframe: HTMLIFrameElement | null = null
  const silentRefreshTimeout: number | null = null
  let silentRefreshPromise: Promise<boolean> | null = null
  let silentRefreshResolve: ((value: boolean) => void) | null = null

  // Handle messages from the silent refresh iframe
  const handleSilentRefreshMessage = (event: MessageEvent) => {
    // Validate origin in production
    try {
      const apiOrigin = new URL(SILENT_REFRESH_URL).origin
      if (import.meta.env.PROD && event.origin !== apiOrigin) {
        return
      }
    } catch (err) {
      console.error('Invalid SILENT_REFRESH_URL:', SILENT_REFRESH_URL, err)
      return
    }

    if (event.data?.type !== 'SILENT_REFRESH_RESPONSE') {
      return
    }

    const { payload } = event.data

    if (payload.success) {
      // Update auth state with new token
      setAuth(payload.user, payload.access_token, payload.expires_in)
      refreshFailureCount.value = 0

      if (silentRefreshResolve) {
        silentRefreshResolve(true)
      }
    } else {
      if (import.meta.env.DEV) {
        console.warn('Silent refresh failed:', payload.error, payload.message)
      }

      // Increment backoff on failure
      refreshFailureCount.value = Math.min(refreshFailureCount.value + 1, 10)

      if (silentRefreshResolve) {
        silentRefreshResolve(false)
      }
    }

    // Cleanup
    cleanupSilentRefreshIframe()
  }

  const cleanupSilentRefreshIframe = () => {
    if (silentRefreshIframe && silentRefreshIframe.parentNode) {
      silentRefreshIframe.parentNode.removeChild(silentRefreshIframe)
    }
    silentRefreshIframe = null
    silentRefreshPromise = null
    silentRefreshResolve = null
  }

  const performSilentRefresh = async (): Promise<boolean> => {
    // If already refreshing via iframe, wait for that
    if (silentRefreshPromise) {
      return silentRefreshPromise
    }

    // Check backoff
    if (!canAttemptRefresh.value) {
      return false
    }

    // Create promise for this refresh attempt
    silentRefreshPromise = new Promise<boolean>((resolve) => {
      silentRefreshResolve = resolve

      // Set timeout for iframe response
      const timeoutId = window.setTimeout(() => {
        if (import.meta.env.DEV) {
          console.warn('Silent refresh timed out, falling back to API refresh')
        }
        cleanupSilentRefreshIframe()
        // Fallback to regular API refresh
        refreshTokenIfNeeded().then(resolve)
      }, SILENT_REFRESH_TIMEOUT_MS)

      // Create hidden iframe
      silentRefreshIframe = document.createElement('iframe')
      silentRefreshIframe.style.display = 'none'
      silentRefreshIframe.style.width = '0'
      silentRefreshIframe.style.height = '0'
      silentRefreshIframe.setAttribute('aria-hidden', 'true')

      // Handle iframe load error
      silentRefreshIframe.onerror = () => {
        clearTimeout(timeoutId)
        cleanupSilentRefreshIframe()
        // Fallback to regular API refresh
        refreshTokenIfNeeded().then(resolve)
      }

      // Set source and append to document
      silentRefreshIframe.src = SILENT_REFRESH_URL
      document.body.appendChild(silentRefreshIframe)

      // Store timeout for cleanup
      const originalResolve = silentRefreshResolve
      silentRefreshResolve = (value: boolean) => {
        clearTimeout(timeoutId)
        originalResolve(value)
      }
    })

    lastRefreshAttempt.value = Date.now()
    return silentRefreshPromise
  }

  // Setup message listener for silent refresh
  if (typeof window !== 'undefined') {
    window.addEventListener('message', handleSilentRefreshMessage)
  }

  // Schedule silent refresh before token expires
  let scheduledRefreshTimeout: number | null = null

  const scheduleSilentRefresh = () => {
    if (scheduledRefreshTimeout) {
      clearTimeout(scheduledRefreshTimeout)
    }

    if (!tokenExpiresAt.value || !isAuthenticated.value) return

    // Schedule refresh 5 minutes before expiry
    const refreshTime = tokenExpiresAt.value - REFRESH_WINDOW_MS - Date.now()

    if (refreshTime > 0) {
      scheduledRefreshTimeout = window.setTimeout(async () => {
        if (isAuthenticated.value && !isRefreshing.value && canAttemptRefresh.value) {
          const success = await performSilentRefresh()
          if (success) {
            // Re-schedule after successful refresh
            scheduleSilentRefresh()
          }
        }
      }, refreshTime)
    } else if (refreshTime > -REFRESH_WINDOW_MS) {
      // Token is expiring soon but not yet expired, refresh now
      if (!isRefreshing.value && canAttemptRefresh.value) {
        performSilentRefresh()
      }
    }
  }

  const cancelSilentRefresh = () => {
    if (scheduledRefreshTimeout) {
      clearTimeout(scheduledRefreshTimeout)
      scheduledRefreshTimeout = null
    }
    cleanupSilentRefreshIframe()
  }

  // Watch for token expiration
  watch(isTokenExpired, async (expired) => {
    if (expired && token.value && !isRefreshing.value) {
      // Attempt refresh before giving up
      const success = await refreshTokenIfNeeded()
      if (!success) {
        stopAutoRefresh()
        logout()
      }
    }
  })

  // Watch for authentication state to manage auto-refresh
  watch(
    isAuthenticated,
    (authenticated) => {
      if (authenticated) {
        startAutoRefresh()
        scheduleSilentRefresh()
        // Sync locale when user logs in
        syncUserLocale()
      } else {
        stopAutoRefresh()
        cancelSilentRefresh()
      }
    },
    { immediate: true },
  )

  // Re-schedule silent refresh and update service worker when token is updated
  watch(tokenExpiresAt, (newExpiry) => {
    if (isAuthenticated.value) {
      scheduleSilentRefresh()
      // Update service worker with new expiry
      updateSessionInServiceWorker(newExpiry, true)
    }
  })

  // Sync user's preferred language with the app locale
  const syncUserLocale = async () => {
    // Priority: user's lang > preferred_lang > organization_lang
    const userLang = user.value?.lang || user.value?.preferred_lang || user.value?.organization_lang

    if (userLang) {
      try {
        const { changeLocale, isLocaleSupported, SUPPORTED_LOCALES } = await import('@/i18n')
        // isLocaleSupported acts as a type guard ensuring userLang is a valid Locale
        if (isLocaleSupported(userLang)) {
          // Cast is safe because isLocaleSupported verified the value
          changeLocale(userLang as (typeof SUPPORTED_LOCALES)[number])
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Failed to sync user locale:', error)
        }
      }
    }
  }

  // Handle window focus/visibility changes
  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && isAuthenticated.value && !isRefreshing.value) {
      // When user returns, check token status
      if (isTokenExpired.value || isTokenExpiringSoon.value) {
        // ATTEMPT REFRESH FIRST instead of immediate logout
        const success = await refreshTokenIfNeeded()
        if (!success && isTokenExpired.value) {
          // Only logout if refresh failed AND token is actually expired
          logout()
        }
      }
    }
  }

  // Set up event listeners
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleVisibilityChange)
  }

  return {
    // State
    user,
    organization,
    organizationZones,
    currentBranch,
    token,
    tokenExpiresAt,
    hasActiveSession, // Renamed from refreshToken (no longer exposed)
    sessionId,
    isRefreshing,
    refreshFailureCount,
    lastActivityTime,

    // Computed
    isAuthenticated,
    isTokenExpired,
    isTokenExpiringSoon,
    timeUntilExpiry,
    currentBackoffDelay,
    canAttemptRefresh,
    userRole,
    userOrganization,
    userDisplayName,
    isAdmin,
    canManageUsers,
    canViewDeliveries,
    canViewFinances,

    // Methods
    setToken,
    setUser,
    updateUser,
    clearAuth,
    logout,
    setAuth,
    setActiveSession,
    memoryOnly,
    setMemoryOnly,
    setRefreshToken, // Legacy compatibility - now just marks session active
    initAuth,
    setRefreshing,
    updateUserLanguage,
    refreshTokenIfNeeded,
    performSilentRefresh,
    startAutoRefresh,
    stopAutoRefresh,
    scheduleSilentRefresh,
    cancelSilentRefresh,
    updateActivityTime,
    hasRole,
    setOrganizationZones,
    loadOrganizationZones,
  }
})
